// Shared constants + parsing helpers mirroring the iOS models.
// Status strings MUST match the live Firestore data exactly.

export const OrderStatus = {
  PENDING: 'Pending',
  CONFIRMED: 'Confirmed',
  PREPARING: 'Preparing',
  READY: 'Ready',
  PICKED_UP: 'Picked Up',
  ON_MY_WAY: 'On My Way',
  IN_DELIVERY: 'In Delivery',
  ARRIVED: 'Arrived',
  DELIVERED: 'Delivered',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

// Statuses written to drivers/{driverId}/acceptedOrders/{orderId}
export const DriverOrderStatus = {
  ACCEPTED: 'Accepted',
  NAVIGATE_TO_RESTAURANT: 'Navigate to Restaurant',
  ARRIVED_AT_RESTAURANT: 'Arrived at Restaurant',
  PICKED_UP_ORDER: 'Picked Up Order',
  ON_MY_WAY: 'On My Way',
  IN_DELIVERY: 'In Delivery',
  ARRIVED_AT_CUSTOMER: 'Arrived at Customer',
  DELIVERED: 'Delivered',
  COMPLETED: 'Completed',
};

// The 6-step driver workflow shown on OrderCompleteCardView
export const DRIVER_WORKFLOW_STEPS = [
  DriverOrderStatus.NAVIGATE_TO_RESTAURANT,
  DriverOrderStatus.ARRIVED_AT_RESTAURANT,
  DriverOrderStatus.PICKED_UP_ORDER,
  DriverOrderStatus.ON_MY_WAY,
  DriverOrderStatus.ARRIVED_AT_CUSTOMER,
  DriverOrderStatus.DELIVERED,
];

// OrderManager.updateDriverOrderStatus mapping: driver status -> main order status
export const DRIVER_TO_ORDER_STATUS = {
  [DriverOrderStatus.ACCEPTED]: OrderStatus.CONFIRMED,
  [DriverOrderStatus.NAVIGATE_TO_RESTAURANT]: OrderStatus.CONFIRMED,
  [DriverOrderStatus.ARRIVED_AT_RESTAURANT]: OrderStatus.CONFIRMED,
  [DriverOrderStatus.PICKED_UP_ORDER]: OrderStatus.PICKED_UP,
  [DriverOrderStatus.ON_MY_WAY]: OrderStatus.ON_MY_WAY,
  [DriverOrderStatus.IN_DELIVERY]: OrderStatus.IN_DELIVERY,
  [DriverOrderStatus.ARRIVED_AT_CUSTOMER]: OrderStatus.ARRIVED,
  [DriverOrderStatus.DELIVERED]: OrderStatus.DELIVERED,
  [DriverOrderStatus.COMPLETED]: OrderStatus.COMPLETED,
};

// Statuses that count toward the 2-delivery driver cap
export const ACTIVE_DRIVER_STATUSES = new Set([
  DriverOrderStatus.ACCEPTED,
  DriverOrderStatus.NAVIGATE_TO_RESTAURANT,
  DriverOrderStatus.ARRIVED_AT_RESTAURANT,
  DriverOrderStatus.PICKED_UP_ORDER,
  DriverOrderStatus.ON_MY_WAY,
  DriverOrderStatus.IN_DELIVERY,
  DriverOrderStatus.ARRIVED_AT_CUSTOMER,
]);

export const MAX_ACTIVE_DELIVERIES = 2;
export const DELIVERY_PROXIMITY_FEET = 200;
export const RESTAURANT_PAYOUT_RATE = 0.90;
export const DRIVER_COMMISSION_RATE = 0.90;
export const PLATFORM_COMMISSION_RATE = 0.10;

// Case-insensitive accepted-order status parsing (mirrors iOS fallback for
// legacy lowercase values).
export function parseDriverStatus(raw) {
  if (!raw || typeof raw !== 'string') return DriverOrderStatus.ACCEPTED;
  const v = raw.trim().toLowerCase();
  const map = {
    'accepted': DriverOrderStatus.ACCEPTED,
    'navigate to restaurant': DriverOrderStatus.NAVIGATE_TO_RESTAURANT,
    'arrived at restaurant': DriverOrderStatus.ARRIVED_AT_RESTAURANT,
    'picked up order': DriverOrderStatus.PICKED_UP_ORDER,
    'picked up': DriverOrderStatus.PICKED_UP_ORDER,
    'on my way': DriverOrderStatus.ON_MY_WAY,
    'in delivery': DriverOrderStatus.IN_DELIVERY,
    'arrived at customer': DriverOrderStatus.ARRIVED_AT_CUSTOMER,
    'arrived': DriverOrderStatus.ARRIVED_AT_CUSTOMER,
    'delivered': DriverOrderStatus.DELIVERED,
    'completed': DriverOrderStatus.COMPLETED,
    'cancelled': 'Cancelled',
  };
  return map[v] || DriverOrderStatus.ACCEPTED;
}

export function parseOrderStatus(raw) {
  if (!raw || typeof raw !== 'string') return OrderStatus.PENDING;
  const v = raw.trim().toLowerCase();
  const found = Object.values(OrderStatus).find(s => s.toLowerCase() === v);
  if (found) return found;
  // Legacy camelCase values written by the validateOrderQRCode function and
  // older clients (e.g. 'pickedUpOrder', 'inDelivery').
  const camel = {
    'pickeduporder': OrderStatus.PICKED_UP,
    'onmyway': OrderStatus.ON_MY_WAY,
    'indelivery': OrderStatus.IN_DELIVERY,
    'arrivedatcustomer': OrderStatus.ARRIVED,
    'arrivedatrestaurant': OrderStatus.CONFIRMED,
    'accepted': OrderStatus.CONFIRMED,
  };
  return camel[v] || raw;
}

// ---------- Formatting ----------
export const formatCurrency = (n) =>
  `$${(Number(n) || 0).toFixed(2)}`;

export const formatDate = (d) => {
  if (!d) return '';
  const date = toDate(d);
  return date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
};

export const formatTime = (d) => {
  if (!d) return '';
  const date = toDate(d);
  return date ? date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';
};

export const formatDateTime = (d) => {
  const date = toDate(d);
  return date ? `${formatDate(date)} ${formatTime(date)}` : '';
};

export const timeAgo = (d) => {
  const date = toDate(d);
  if (!date) return '';
  const s = Math.floor((Date.now() - date.getTime()) / 1000);
  if (s < 60) return 'Just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

// Firestore Timestamp | Date | seconds-obj | ISO string -> Date|null
export function toDate(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v.toDate === 'function') return v.toDate();
  if (typeof v === 'object' && 'seconds' in v) return new Date(v.seconds * 1000);
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

// GeoPoint | {latitude,longitude} | {lat,lng} | {lat,lon} -> {lat, lng} | null
export function toLatLng(v) {
  if (!v) return null;
  const lat = v.latitude ?? v.lat;
  const lng = v.longitude ?? v.lng ?? v.lon;
  if (lat == null || lng == null) return null;
  return { lat: Number(lat), lng: Number(lng) };
}

// Great-circle distance in feet.
export function distanceFeet(a, b) {
  if (!a || !b) return null;
  const R = 20902231; // earth radius in feet
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLng = (b.lng - a.lng) * Math.PI / 180;
  const s = Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

export function distanceMiles(a, b) {
  const ft = distanceFeet(a, b);
  return ft == null ? null : ft / 5280;
}

export const statusColor = (status) => ({
  'Pending': 'orange',
  'Confirmed': 'blue',
  'Preparing': 'purple',
  'Ready': 'green',
  'Picked Up': 'indigo',
  'On My Way': 'cyan',
  'In Delivery': 'cyan',
  'Arrived': 'teal',
  'Delivered': 'green',
  'Completed': 'green',
  'Cancelled': 'red',
}[status] || 'gray');

// Payout calculations (mirror iOS exactly)
export const restaurantPayout = (order) => {
  const base = order.subtotal ?? order.totalAmount ?? 0;
  return Number(order.restaurantPayout ?? base * RESTAURANT_PAYOUT_RATE);
};
export const driverPayoutFor = (order) =>
  Number(order.driverPayout ?? ((order.deliveryFee || 0) * DRIVER_COMMISSION_RATE) + (order.tipAmount || 0));

// ---------- Order normalization ----------
// Converts a Firestore order doc into a plain object matching the iOS Order model.
export function parseOrder(id, d) {
  const items = (d.orderItems || d.items || []).map(it => {
    // Web checkout nests item details inside a `menuItem` object
    const mi = it.menuItem || {};
    const price = it.price ?? mi.price ?? 0;
    return {
      id: it.id || it.menuItemId || mi.id || '',
      menuItemId: it.menuItemId || it.id || mi.id || '',
      menuItemName: it.menuItemName || it.name || mi.name || 'Item',
      name: it.menuItemName || it.name || mi.name || 'Item',
      quantity: it.quantity || 1,
      price,
      totalPrice: it.totalPrice ?? price * (it.quantity || 1),
      specialInstructions: it.specialInstructions || '',
      imageURL: it.imageURL || it.imageUrl || it.image || mi.menuItemImageURL || mi.imageURL || '',
    };
  });
  return {
    id,
    orderId: d.orderId || id,
    orderNumber: d.orderNumber || d.orderId || id,
    customerId: d.customerId || '',
    customerName: d.customerName || '',
    customerPhone: d.customerPhone || '',
    restaurantId: d.restaurantId || '',
    restaurantName: d.restaurantName || '',
    orderItems: items,
    items,
    orderType: d.orderType || 'Delivery',
    status: parseOrderStatus(d.status),
    rawStatus: d.status,
    subtotal: d.subtotal || 0,
    chefServiceFee: d.chefServiceFee ?? d.serviceFee ?? 0,
    deliveryFee: d.deliveryFee || 0,
    totalAmount: d.totalAmount || d.total || 0,
    total: d.totalAmount || d.total || 0,
    paymentMethod: d.paymentMethod || '',
    paymentStatus: d.paymentStatus || '',
    paymentProvider: d.paymentProvider || '',
    providerTransactionId: d.providerTransactionId || d.stripePaymentIntentId || '',
    jadAuthorizationId: d.jadAuthorizationId || '',
    restaurantLocation: d.restaurantLocation || null,
    deliveryAddress: d.deliveryAddress || null,
    customerAddress: d.customerAddress || d.deliveryAddress?.street || '',
    driverId: d.driverId || null,
    driverName: d.driverName || '',
    driverPayout: d.driverPayout,
    driverCommission: d.driverCommission,
    platformCommission: d.platformCommission,
    restaurantPayout: d.restaurantPayout,
    tipAmount: d.tipAmount || 0,
    specialInstructions: d.specialInstructions || d.deliveryInstructions || '',
    estimatedDeliveryTime: toDate(d.estimatedDeliveryTime),
    pickupTime: toDate(d.pickupTime),
    qrCodeData: d.qrCodeData || '',
    qrCodeActive: d.qrCodeActive,
    qrGeneratedAt: toDate(d.qrGeneratedAt),
    qrScannedAt: toDate(d.qrScannedAt),
    restaurantCompleted: d.restaurantCompleted,
    cancellationReason: d.cancellationReason || '',
    cancelledBy: d.cancelledBy || '',
    createdAt: toDate(d.createdAt),
    updatedAt: toDate(d.updatedAt),
    raw: d,
  };
}

// Accepted-order subcollection doc -> plain object (DriverAcceptedOrder model)
export function parseAcceptedOrder(id, d) {
  return {
    id,
    orderId: d.orderId || id,
    driverId: d.driverId || '',
    status: parseDriverStatus(d.status),
    acceptedAt: toDate(d.acceptedAt),
    estimatedPickupTime: toDate(d.estimatedPickupTime),
    actualPickupTime: toDate(d.actualPickupTime),
    estimatedDeliveryTime: toDate(d.estimatedDeliveryTime),
    actualDeliveryTime: toDate(d.actualDeliveryTime),
    pickupLocation: toLatLng(d.pickupLocation) || toLatLng(d.restaurantLocation?.coordinates),
    deliveryLocation: toLatLng(d.deliveryLocation),
    totalEarnings: d.totalEarnings ?? d.driverPayout ?? 0,
    driverPayout: d.driverPayout ?? 0,
    driverCommission: d.driverCommission ?? 0,
    platformCommission: d.platformCommission ?? 0,
    deliveryFee: d.deliveryFee ?? 0,
    tipAmount: d.tipAmount ?? 0,
    customerId: d.customerId || '',
    customerName: d.customerName || '',
    customerPhone: d.customerPhone || '',
    customerAddress: d.customerAddress || d.deliveryAddress?.street || '',
    deliveryAddress: d.deliveryAddress || null,
    restaurantId: d.restaurantId || '',
    restaurantName: d.restaurantName || '',
    items: d.orderItems || d.items || [],
    subtotal: d.subtotal ?? 0,
    chefServiceFee: d.chefServiceFee ?? 0,
    totalAmount: d.totalAmount ?? 0,
    distance: d.distance ?? 0,
    specialInstructions: d.specialInstructions || '',
    createdAt: toDate(d.createdAt),
    updatedAt: toDate(d.updatedAt),
    raw: d,
  };
}
